import axios, { AxiosError } from 'axios'
import moment from 'moment'
import { Store } from 'redux'

import { appConfig } from '@/app-config/appConfig'
import { entityGenerator } from '@/lib/entityGenerator'
import { setVerificationRedirectData } from '@/lib/redirect-utils'
import { redirectTo3DSVerification } from '@/lib/redirectTo3DSVerification'
import { loadData } from '@/lib/requests/loadData'
import { BookTripDetailsDefault } from '@/organisms/ItinerarySubmit/types'
import { IAction } from '@/redux/actions/action-helpers'
import { Actions as TripApprovalsActions } from '@/redux/actions/trip-approvals.actions'
import { Actions as TripActions } from '@/redux/actions/trips.actions'
import { ApplicationState } from '@/redux/stores/types'
import { brightRed, darkBlue, darkRed, linkBlue, purple, secondaryBlack, successGreen, yellow } from '@/refactor/colors'
import { ErrorsInBooking } from '@/types/errors'
import { BookingSource, TripBookingStatusResponse, TripDirection, TripStatus } from '@/types/trip'

import { AllTripStatuses, InvoiceProfileStatus, Trip, TripApproval, TripStatusVirtual } from './index'
import { WorldlineOrderStatus } from './worldline'

export const emptyTrip = entityGenerator<Trip>({
	purpose_list: [],
	id: 0,
	status: TripStatus.Draft,
	trip_type: TripDirection.RoundTrip,
	include_flights: true,
	include_accommodations: false,
	travelers_count: 0,
	travelers: [],
	flights_comments: null,
	hotels_comments: null,
	cars_comments: null,
	products: [],
	approval_statuses: [],
	support_requests: [],
	purpose: [],
	total_cost_billed: [],
	total_cost: 0,
	sub_trips: [],
	end_date: '',
	start_date: '',
})

export function calculateTripStatus(trip: Trip): AllTripStatuses {
	const now = moment.now()
	if (!trip.products?.length) {
		return trip.status
	}

	const firstDest = trip.products[0]
	const startTime = moment(firstDest.start_time)
	const endTime = moment(trip.products[trip.products.length - 1].end_time)

	if (trip.status !== TripStatus.Booked) {
		return trip.status
	}

	if (endTime.valueOf() < now) {
		return TripStatusVirtual.PastTrip
	}

	if (startTime.subtract(1, 'week').valueOf() <= now) {
		return TripStatusVirtual.Upcoming
	}

	if (startTime.valueOf() >= now) {
		return TripStatus.Booked
	}

	return TripStatusVirtual.TravelingNow
}

export type TripStatusTabType = TripStatus | TripStatusVirtual

export const tripOrderByStatus = (tripStatus: TripStatusTabType) =>
	({
		[TripStatus.WaitingApproval]: 3,
		[TripStatus.SendingToApproval]: 3,
		[TripStatus.Draft]: 2,
		[TripStatus.DraftBySupport]: 1,
		[TripStatus.Reverted]: 0,
		[TripStatus.Booked]: 4,
		[TripStatus.NotApproved]: 1,
		[TripStatus.Canceled]: 6,
		[TripStatusVirtual.PastTrip]: 1,
		[TripStatusVirtual.Rejected]: 6,
		[TripStatusVirtual.TravelingNow]: 2,
		[TripStatusVirtual.Upcoming]: 3,
		[TripStatus.Approved]: 6,
		[TripStatus.Booking]: 6,
		[TripStatus.PriceChanged]: 6,
		[TripStatus.PreTripGreenLight]: 6,
		[TripStatus.WaitingForUserSubmission]: 6,
		[TripStatus.UrgentActionRequired]: 6,
	})[tripStatus]

const baseUrl = appConfig.API_DOMAIN

// TASK move to redux actions
export async function getApprovalStatus(tripId: number): Promise<TripApproval[]> {
	const { data } = await loadData<TripApproval[]>({ resourcePath: `trips/${tripId}/approvals_status` })
	return data
}

export function isTripBooked(trip: Trip) {
	return [TripStatus.Booked].includes(trip.status)
}

export async function downloadItinerary(tripId: number, userId?: number | 'all') {
	const ext = userId === 'all' ? 'zip' : 'pdf'
	const hash = await axios.get<string>(`${baseUrl}/trips/${tripId}/itinerary/hash`, {
		params: { user_id: userId },
	})
	return `${baseUrl}/trips/${tripId}/itinerary/${hash.data}.${ext}`
}

export async function downloadCalendarEvent(tripId: number, userId: number) {
	const hash = await axios.get<string>(`${baseUrl}/trips/${tripId}/itinerary/hash`, {
		params: { user_id: userId },
	})
	return `${baseUrl}/trips/${tripId}/itinerary/${hash.data}.ics`
	// additional options
	// return `webcal${baseUrl?.substring(4)}/trips/${tripId}/itinerary/${hash.data}.ics`
	// return 'http://www.google.com/calendar/event?action=TEMPLATE&dates=20200206T010000Z%2F20200208T010000Z&text=test&location=test%20location&details=test%20description'
}

export const StatusColorMap: Partial<Record<TripStatus | TripStatusVirtual | InvoiceProfileStatus, string>> = {
	[TripStatus.Draft]: darkBlue,
	[TripStatus.DraftBySupport]: darkBlue,
	[TripStatus.Booking]: linkBlue,
	[TripStatus.PriceChanged]: linkBlue,
	[TripStatus.Approved]: purple,
	[TripStatus.WaitingApproval]: yellow,
	[InvoiceProfileStatus.ConfirmationInProgress]: yellow,
	[InvoiceProfileStatus.TermsOfServiceValidationPending]: yellow,
	[InvoiceProfileStatus.Active]: successGreen,
	[TripStatus.Booked]: successGreen,
	[TripStatusVirtual.PastTrip]: purple,
	[TripStatus.Canceled]: secondaryBlack,
	[TripStatus.NotApproved]: darkRed,
	[TripStatusVirtual.TravelingNow]: successGreen,
	[TripStatusVirtual.Upcoming]: yellow,
	[TripStatus.Reverted]: darkBlue,
	[TripStatus.WaitingForUserSubmission]: purple,
	[TripStatus.UrgentActionRequired]: brightRed,
}

/* #region  BookingError */
// eslint-disable-next-line no-restricted-syntax
export enum BookingErrorReason {
	MissingInfo = 'missing_info',
	ServerError = 'server_error',
	PriceChange = 'price_change',
	PaymentPermissionDenied = 'payment_permission_denied',
}

export class BookingError extends Error {
	private static REASON_LABELS: Record<BookingErrorReason, string> = {
		[BookingErrorReason.MissingInfo]: 'Missing Details',
		[BookingErrorReason.ServerError]: 'Booking Error',
		[BookingErrorReason.PriceChange]: 'Price Change',
		[BookingErrorReason.PaymentPermissionDenied]: 'Payment Permission Denied',
	}
	private static SOURCE_LABELS: Record<BookingSource, string> = {
		[BookingSource.ApproveOrReject]: 'Approve Or Reject',
		[BookingSource.Book]: 'Book',
		[BookingSource.BookBySupport]: 'Book by Support',
		[BookingSource.SubmitForApproval]: 'Sent for Approval',
	}
	public reason: BookingErrorReason
	public source: BookingSource
	public message!: string
	public originalError: Error | undefined
	public errors: Array<ErrorsInBooking> | undefined

	constructor(
		reason: BookingErrorReason,
		source: BookingSource,
		originalError?: Error,
		errors?: Array<ErrorsInBooking>,
	) {
		const reasonString = BookingError.reasonToString(reason)
		super(reasonString)
		this.reason = reason
		this.source = source
		this.originalError = originalError
		this.errors = errors
	}

	public get reasonString(): string {
		return BookingError.reasonToString(this.reason)
	}

	public get sourceString(): string {
		return BookingError.sourceToString(this.source)
	}

	public static reasonToString(reason: BookingErrorReason): string {
		return BookingError.REASON_LABELS[reason]
	}

	public static sourceToString(source: BookingSource): string {
		return BookingError.SOURCE_LABELS[source]
	}
}
/* #endregion */

export class PriceChangeBookingError extends BookingError {}

export class BookingValidationError extends BookingError {}

/* #region Submit Trip */

const defaultShouldStopPolling = ({ status }: TripBookingStatusResponse) => status !== TripStatus.Approved

export async function pollBookingStatus(
	store: Store<ApplicationState, IAction>,
	tripId: number,
	shouldStopPolling = defaultShouldStopPolling,
): Promise<TripBookingStatusResponse> {
	let data: TripBookingStatusResponse
	let errorsCount = 0

	// eslint-disable-next-line no-constant-condition
	while (true) {
		try {
			data = await new Promise<TripBookingStatusResponse>((resolve, reject) => {
				store.dispatch(
					TripActions.getBookingStatus(
						tripId,
						(data) => resolve(data),
						(error) => reject(error),
					),
				)
			})

			if (shouldStopPolling(data)) {
				break
			}

			await new Promise((resolve) => setTimeout(resolve, 2000))
		} catch (e) {
			if (errorsCount > 5) {
				throw e
			}

			errorsCount += 1
			await new Promise((resolve) => setTimeout(resolve, 2000))
		}
	}

	return data
}

/** Throws `BookingError` on failure */
interface SubmitProps extends Partial<BookTripDetailsDefault> {
	store: Store<ApplicationState, IAction>
	trip: Trip
	source: BookingSource
}

export async function checkTripBookingResultErrors({
	store,
	tripId,
	changes,
	source,
}: {
	store: Store<ApplicationState, IAction>
	tripId: number
	changes: TripBookingStatusResponse
	source: BookingSource
}) {
	if (changes?.status === TripStatus.Reverted) {
		throw new BookingError(BookingErrorReason.ServerError, source, undefined, changes.errors)
	}

	if (changes?.status === TripStatus.WaitingApproval) {
		store.dispatch(TripApprovalsActions.getTripApprovals(tripId))
	}

	if (changes?.status === TripStatus.PriceChanged) {
		throw new PriceChangeBookingError(BookingErrorReason.PriceChange, source)
	}

	const statusUpdate = await pollBookingStatus(store, tripId, ({ status }) => status !== TripStatus.SendingToApproval)
	if (statusUpdate && statusUpdate.status === TripStatus.Draft) {
		throw new BookingError(BookingErrorReason.ServerError, source, undefined, statusUpdate.errors)
	}
}

function handleTripBookingError(err: AxiosError, source: BookingSource) {
	if (err.response?.status === 412) {
		throw new BookingError(BookingErrorReason.MissingInfo, source, err)
	}

	if (err.response?.status === 400) {
		throw new BookingValidationError(BookingErrorReason.ServerError, source, err)
	}

	if (err.response?.status !== 403) {
		throw new BookingError(BookingErrorReason.ServerError, source, err)
	}

	throw new BookingError(BookingErrorReason.ServerError, source, err)
}

export async function pollTripStatusWithHandlingErrorsAndRedirects(
	store: Store<ApplicationState, IAction>,
	tripId: number,
	source: BookingSource,
) {
	const changes = await pollBookingStatus(
		store,
		tripId,
		({ status, trip_fees, product_fees }) =>
			![TripStatus.Approved, TripStatus.SendingToApproval].includes(status) ||
			trip_fees.some(({ status }) => status === WorldlineOrderStatus.Redirected) ||
			(product_fees ?? []).some(({ status }) => status === WorldlineOrderStatus.Redirected),
	)
	const redirectTrip = changes.trip_fees.find(({ status }) => status === WorldlineOrderStatus.Redirected)?.order
		?.redirect_url

	const redirectProduct = (changes.product_fees ?? []).find(({ status }) => status === WorldlineOrderStatus.Redirected)
		?.order?.redirect_url

	const redirect = redirectTrip || redirectProduct
	if (redirect) {
		setVerificationRedirectData<{ trip_id: number }>({
			returnToUrl: `/trips/${tripId}/itinerary`,
			data: { trip_id: tripId },
		})
		redirectTo3DSVerification(redirect)
	} else {
		await checkTripBookingResultErrors({ store, tripId, changes, source })
	}
	return changes
}

export async function submitTrip({
	store,
	trip,
	message,
	source,
	trip_name,
	fake_booking,
	card_cvc_data,
	selected_documents,
	report_settings,
	rails_ticket_delivery_options,
}: SubmitProps): Promise<void> {
	const tripId = trip.id!

	return new Promise((res, rej) => {
		const _handleSuccess = async () => {
			pollTripStatusWithHandlingErrorsAndRedirects(store, tripId, source).then(() => res(), rej)
		}

		function _handleErr(err: AxiosError) {
			try {
				handleTripBookingError(err, source)
			} catch (e) {
				rej(e)
			}
		}

		store.dispatch(
			TripActions.submitTrip(
				tripId,
				source,
				{
					trip_name,
					message,
					selected_documents,
					fake_booking,
					card_cvc_data,
					report_settings,
					rails_ticket_delivery_options,
				},
				_handleSuccess,
				_handleErr,
			),
		)
	})
}

const VALID_STATUSES_AFTER_VERIFICATION: TripStatus[] = [
	TripStatus.Booked,
	TripStatus.WaitingApproval,
	TripStatus.WaitingForUserSubmission,
]

export async function checkTripStatusAfterVerification({
	store,
	tripId,
}: {
	store: Store<ApplicationState, IAction>
	tripId: number
}) {
	const statusUpdate = await pollBookingStatus(
		store,
		tripId,
		({ status }) => ![TripStatus.Booking, TripStatus.SendingToApproval].includes(status),
	)

	if (statusUpdate?.status && VALID_STATUSES_AFTER_VERIFICATION.includes(statusUpdate.status)) {
		return statusUpdate
	}

	throw new BookingError(BookingErrorReason.ServerError, BookingSource.Book, undefined, statusUpdate.errors)
}

export const getApproverName = (trip: Trip) => {
	const approver = trip.approval_statuses[0]?.user
	const approverName = `${approver?.first_name} ${approver?.last_name}`
	const isTripStatusWaitingForUserSubmission =
		trip.calculatedStatus === TripStatus.WaitingForUserSubmission || trip.status === TripStatus.WaitingForUserSubmission

	return isTripStatusWaitingForUserSubmission ? approverName : undefined
}

export function allowExternalServices(trip: Trip) {
	return [TripStatus.Booked, TripStatus.Draft, TripStatus.DraftBySupport, TripStatus.Reverted].includes(trip.status)
}
